import { Select, Space, Typography } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ORANGE1, PURPLE1 } from '../../../constants/color';
import { EXTENDED_TIMEOUT } from '../../../constants/systemConstants';
import { UserListData } from '../../../types';
import { alertMessage } from '../../../utils/alertMessage';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';

type UsersDropdownProps = {
  disabled?: boolean;
  defaultOptions?: boolean;
  initialValue?: any;
  /**
   * 编辑场景下传入当前已选用户, 让下拉框打开时直接显示用户名 (而不是裸 userId)。
   * 只需要 userId + userName, 其他字段可选; nickName/mobilePhone 提供则在选项里一并展示。
   */
  initialUser?: Partial<UserListData> & { userId: number; userName: string };
  onChange?:
    | ((value: any, option: DefaultOptionType | DefaultOptionType[]) => void)
    | undefined;
};

const UsersDropdown = ({
  onChange,
  disabled = false,
  defaultOptions = false,
  initialValue,
  initialUser,
}: UsersDropdownProps) => {
  const { t } = useTranslation();
  //Data components
  const [userList, setUserList] = useState<UserListData[]>(
    initialUser ? [initialUser as UserListData] : []
  );
  //Pagination/table Components
  const [userPage, setUserPage] = useState(2);
  const [userTotalPage, setUserTotalPage] = useState(2);
  const [searchText, setSearchText] = useState<{ [key: string]: string }>({});
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();

  // 是否已经从服务端拉过数据 (有 initialUser 时 userList 不为空, 不能光靠 length 判断)
  const [hasFetched, setHasFetched] = useState(false);

  /**
   * Sends a request and returns the User Data based on the typed keywords
   */
  const getUserData = (value?: string) => {
    getDataWithAuthToken('users/list', {
      params: {
        keyword: value || undefined,
      },
    })
      .then((response) => {
        if (response && response.goodStatus) {
          let list: UserListData[] = response.data.list;
          // 保留 initialUser 在选项里, 避免它被分页/搜索结果挤掉后, 已选值显示成裸 userId
          if (
            initialUser &&
            !list.some((u) => u.userId === initialUser.userId)
          ) {
            list = [initialUser as UserListData, ...list];
          }
          setUserList(list);
          setUserPage(2);
          setUserTotalPage(response.data.totalPage);
          setHasFetched(true);
        } else
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /**
   * Makes a new request to get new list of users
   * When the user scrolls to the bottom of the dropdown box
   */
  const fetchOnScrollUserData = (e: React.UIEvent) => {
    e.persist();
    let target = e.target as HTMLDivElement;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
      if (userPage > userTotalPage) return;
      getDataWithAuthToken('users/list', {
        params: {
          page: userPage,
          keyword: searchText.user || undefined,
        },
      }).then((response) => {
        if (response && response.goodStatus) {
          setUserList((prev) => [...prev, ...response.data.list]);
          setUserPage((prev) => prev + 1);
          setUserTotalPage(response.data.totalPage);
        }
      });
    }
  };

  const handleOnChange = (value: any, options: any) => {
    if (onChange) {
      onChange(value, options);
    }
  };

  return (
    <Select
      style={{ width: '100%' }}
      filterOption={false}
      placeholder={t('searchPlaceholders.searchUserKeyword')}
      // 让下拉浮层宽度按内容自适应, 而不是跟随选择框宽度, 避免长内容被截断
      dropdownMatchSelectWidth={false}
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
      onPopupScroll={fetchOnScrollUserData}
      onFocus={() => {
        if (!hasFetched) getUserData();
      }}
      showSearch
      onSearch={(value: string) => {
        setSearchText((prev) => ({ ...prev, user: value }));
        if (typingTimeout) clearTimeout(typingTimeout);
        setTypingTimeout(
          setTimeout(() => getUserData(value), EXTENDED_TIMEOUT)
        );
      }}
      disabled={disabled}
      allowClear={!defaultOptions}
      defaultValue={initialValue ? initialValue : undefined}
      optionLabelProp="key"
      onChange={handleOnChange}
    >
      {defaultOptions && (
        <Select.Option key={0} value={0}>
          {t('actionsColumn.unassigned')}
        </Select.Option>
      )}
      {userList.map((user) => (
        <Select.Option key={user.userName} value={user.userId} user={user}>
          <div>
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography.Text type="secondary">
                {t('users.userListColumns.username')}
              </Typography.Text>
              <Typography.Text type="secondary">
                {t('users.userListColumns.mobilePhone')}
              </Typography.Text>
            </span>
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Space>
                <Typography>
                  {user.userName}
                  {user.nickName ? ` (${user.nickName})` : ''}
                </Typography>
                {user.userRank && user.userRank.rankId > 0 && (
                  <Typography.Text style={{ color: ORANGE1 }}>
                    {`| ${user.userRank.rankName}`}
                  </Typography.Text>
                )}
                {user.member && user.member.rankId > 0 && (
                  <Typography.Text style={{ color: PURPLE1 }}>
                    {`| ${user.member.rankName}`}
                  </Typography.Text>
                )}
              </Space>
              <Typography>{user.mobilePhone}</Typography>
            </span>
          </div>
        </Select.Option>
      ))}
    </Select>
  );
};

export default UsersDropdown;
